import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  DeviceType,
  ApiCisternRequestDto,
  ApiCisternResponseDto,
} from '@birco/rainmesh-api';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, map } from 'rxjs';
import {
  InputWrapperComponent,
  isSubmitAllowed,
} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { DeviceActions } from '../../../devices/state/device.actions';
import { selectAllDevices } from '../../../devices/state/device.selectors';
import { CisternActions } from '../../state/cistern.actions';
import {
  selectAllCisterns,
  selectCisternError,
  selectIsCisternsLoading,
} from '../../state/cistern.selectors';
import { ErrorMessageComponent } from '../../../../shared/components/error/error-message/error-message.component';

type CisternDialogData = {
  mode: 'create' | 'update';
  cistern?: ApiCisternResponseDto;
};

export type CisternDialogResult = {
  id?: string;
  requestDto: ApiCisternRequestDto;
};

@Component({
  imports: [
    AsyncPipe,
    MatDialogModule,
    TranslatePipe,
    ReactiveFormsModule,
    InputWrapperComponent,
    ErrorMessageComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dialog-update-cistern.component.html',
  styleUrl: './dialog-update-cistern.component.scss',
})
export class DialogUpdateCisternComponent {
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  data = inject<CisternDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DialogUpdateCisternComponent>);
  readonly apiError$ = this.store
    .select(selectCisternError)
    .pipe(map((error) => this.translateApiError(error)));
  readonly isLoading$ = this.store.select(selectIsCisternsLoading);

  readonly form: FormGroup = this.fb.group({
    name: [
      this.data?.cistern?.name ?? '',
      [Validators.required, DialogUpdateCisternComponent.noWhitespaceValidator],
    ],
    literCapacity: [
      this.data?.cistern?.literCapacity ?? null,
      [Validators.min(0)],
    ],
    maxWaterLevelDistance: [
      this.data?.cistern?.maxWaterLevelDistance ?? null,
      [Validators.min(0)],
    ],
    floorDistance: [
      this.data?.cistern?.floorDistance ?? null,
      [Validators.min(0)],
    ],
    distanceSensorId: [
      this.data?.cistern?.distanceSensor?.id ?? null,
      Validators.required,
    ],
  });

  readonly distanceSensors$ = combineLatest([
    this.store.select(selectAllDevices),
    this.store.select(selectAllCisterns),
  ]).pipe(
    map(([devices, cisterns]) => {
      const usedSensorIds = new Set(
        cisterns
          .map((cistern) => cistern.distanceSensor?.id)
          .filter((sensorId): sensorId is string => !!sensorId),
      );

      const currentSensorId = this.data?.cistern?.distanceSensor?.id;
      if (currentSensorId) {
        usedSensorIds.delete(currentSensorId);
      }

      return devices
        .filter((device) => device.type === DeviceType.DISTANCE)
        .filter((device) => !usedSensorIds.has(device.id))
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name));
    }),
  );

  constructor() {
    this.store.dispatch(
      DeviceActions.public.loadAllDevicesByFilters({ filter: {} }),
    );
    this.store.dispatch(
      CisternActions.public.loadAllCisternsByFilters({ filter: {} }),
    );

    this.form.controls['distanceSensorId'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === '') {
          this.form.controls['distanceSensorId'].setValue(null, {
            emitEvent: false,
          });
        }
      });
  }

  get titleKey(): string {
    return this.data.mode === 'create'
      ? 'cisterns.create.title'
      : 'cisterns.edit.title';
  }

  onSubmit() {
    if (!isSubmitAllowed(this.form)) {
      return;
    }

    const payload: ApiCisternRequestDto = {
      name: (this.form.value.name ?? '').trim(),
      literCapacity: this.toNullableNumber(this.form.value.literCapacity),
      maxWaterLevelDistance: this.toNullableNumber(
        this.form.value.maxWaterLevelDistance,
      ),
      floorDistance: this.toNullableNumber(this.form.value.floorDistance),
      distanceSensorId: this.form.value.distanceSensorId || null,
    };

    this.dialogRef.close({
      id: this.data.cistern?.id,
      requestDto: payload,
    } satisfies CisternDialogResult);
  }

  private translateApiError(error: any): string {
    if (!error) {
      return '';
    }

    const rawError = [error?.error?.message, error?.error, error?.message]
      .filter(Boolean)
      .map((value) => String(value))
      .join(' ')
      .toLowerCase();

    if (
      rawError.includes('in use') ||
      rawError.includes('already assigned') ||
      rawError.includes('already used') ||
      rawError.includes('already exists') ||
      rawError.includes('distance sensor')
    ) {
      return this.translate.instant('cisterns.form.distanceSensorInUse');
    }

    return (
      error?.error?.message ||
      error?.error ||
      error?.message ||
      this.translate.instant('cisterns.form.saveError')
    );
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }

  private static noWhitespaceValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = String(control.value ?? '');
    return value.trim().length > 0 ? null : { required: true };
  }
}
